import React from 'react';
import { AppState } from '../../redux/initialStates/AppState';
import { connect, ConnectedProps } from 'react-redux';
import { ILocationState } from '../../locations/redux/LocationState';
import LayoutContent from '../../components/AppLayout/LayoutContent';
import { Form, FormControl, FormActionButtons } from '../../components/Form';
import TextInput from '../../components/TextInput/TextInput';
import { IUsersForm } from '../../redux/initialStates/users/usersForm';
import {
  updateFirstName,
  updateLastName,
  updateEmail,
  addUserRole,
  removeUserRole,
  updateRole,
  addUserRoleLocation,
  removeUserRoleLocation,
  validateUserForm,
  initializeUserFormData,
  submitUserForm,
} from '../../redux/actions/usersActions/usersFormActions';
import UserPermissionsGroup from '../../components/UserPermissions/UserPermissionsGroup';
import { useParams, useHistory } from 'react-router-dom';
import { ContextParams } from 'types/ContextParams.interface';

type Props = ReduxProps & {
  location: ILocationState;
  usersForm: IUsersForm;
  usersFormCache: IUsersForm;
  addingLocations: boolean;
};

const UsersCreatePage = (props: Props) => {
  /* ****** Props/State ****** */

  const {
    usersForm,
    updateFirstName,
    updateLastName,
    updateEmail,
    addUserRole,
    removeUserRole,
    updateRole,
    addUserRoleLocation,
    removeUserRoleLocation,
    validateUserForm,
    initializeUserFormData,
    submitUserForm,
  } = props;

  /* ****** Hooks ****** */

  const { contextId: companyId } = useParams<ContextParams>();
  const history = useHistory();

  /* ****** Effects ****** */

  // Clear data when leaving the form
  React.useEffect(() => {
    return () => {
      initializeUserFormData();
    };
  }, [initializeUserFormData]);

  /* ****** Callbacks ****** */

  const handleUserFormValidation = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const isValid = validateUserForm(usersForm);

    if (isValid) {
      const newUser = await submitUserForm(usersForm, companyId, '');

      if (newUser && newUser.id) {
        history.push(`/companies/${companyId}/users/${newUser.id}`);
      }
    }
  };

  const handleUserFormCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    history.push(`/companies/${companyId}/users`);
  };

  return (
    <LayoutContent
      title='New User'
      loadingContent={usersForm.loading}
      pageId='users-create-page'
      containerType='within'
    >
      <Form onSubmit={handleUserFormValidation}>
        <FormControl
          label='First Name'
          withError={usersForm.validation.usersForm.firstName.hasError}
          errorMessage={usersForm.validation.usersForm.firstName.error}
        >
          <TextInput
            placeholder='e.g. John'
            name='given_name'
            onChange={(event) => {
              updateFirstName(event.target.value);
            }}
            value={usersForm.firstName}
            loading={usersForm.processing}
          />
        </FormControl>
        <FormControl
          label='Last Name'
          withError={usersForm.validation.usersForm.lastName.hasError}
          errorMessage={usersForm.validation.usersForm.lastName.error}
        >
          <TextInput
            placeholder='e.g. Doe'
            name='family_name'
            onChange={(event) => {
              updateLastName(event.target.value);
            }}
            value={usersForm.lastName}
            loading={usersForm.processing}
          />
        </FormControl>
        <FormControl
          label='Email Address'
          withError={usersForm.validation.usersForm.email.hasError}
          errorMessage={usersForm.validation.usersForm.email.error}
        >
          <TextInput
            placeholder='e.g. name@domain.com'
            name='email'
            onChange={(event) => {
              updateEmail(event.target.value);
            }}
            value={usersForm.email}
            loading={usersForm.processing}
          />
        </FormControl>
        <FormControl label='Security Role(s)'>
          <UserPermissionsGroup
            companyId={companyId}
            onAddUserRole={addUserRole}
            onRemoveUserRole={removeUserRole}
            onUpdateRole={updateRole}
            onAddLocation={addUserRoleLocation}
            onRemoveLocation={removeUserRoleLocation}
            value={usersForm.roles}
            validation={usersForm.validation}
            loading={usersForm.processing}
          />
        </FormControl>
        <FormActionButtons handleCancel={handleUserFormCancel} loading={usersForm.processing} />
      </Form>
    </LayoutContent>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    usersForm: state.users.usersForm,
    usersFormCache: state.cache.usersFormCache,
  };
};

const actionCreators = {
  updateFirstName,
  updateLastName,
  updateEmail,
  addUserRole,
  removeUserRole,
  addUserRoleLocation,
  updateRole,
  removeUserRoleLocation,
  validateUserForm,
  initializeUserFormData,
  submitUserForm,
};

const connector = connect(mapStateToProps, actionCreators);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(UsersCreatePage);
